/**
 * Comprehensive error handling utilities for the enhanced phonebook feature
 */

export interface ErrorDetails {
  code: string;
  message: string;
  userMessage: string;
  actionable: boolean;
  retryable: boolean;
  suggestions?: string[];
}

export interface RetryConfig {
  maxAttempts: number;
  baseDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
}

export interface NetworkError extends Error {
  status?: number;
  code?: string;
  retryable?: boolean;
}

// Default retry configuration
export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxAttempts: 3,
  baseDelay: 1000,
  maxDelay: 10000,
  backoffMultiplier: 2
};

// Error type definitions
export enum ErrorType {
  NETWORK = 'NETWORK',
  VALIDATION = 'VALIDATION',
  FILE_PROCESSING = 'FILE_PROCESSING',
  PERMISSION = 'PERMISSION',
  QUOTA = 'QUOTA',
  UNKNOWN = 'UNKNOWN'
}

// File processing error codes
export enum FileErrorCode {
  INVALID_FORMAT = 'INVALID_FORMAT',
  FILE_TOO_LARGE = 'FILE_TOO_LARGE',
  CORRUPTED_FILE = 'CORRUPTED_FILE',
  EMPTY_FILE = 'EMPTY_FILE',
  PARSING_FAILED = 'PARSING_FAILED',
  UNSUPPORTED_ENCODING = 'UNSUPPORTED_ENCODING'
}

// Network error codes
export enum NetworkErrorCode {
  CONNECTION_FAILED = 'CONNECTION_FAILED',
  TIMEOUT = 'TIMEOUT',
  SERVER_ERROR = 'SERVER_ERROR',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  NOT_FOUND = 'NOT_FOUND',
  RATE_LIMITED = 'RATE_LIMITED',
  QUOTA_EXCEEDED = 'QUOTA_EXCEEDED'
}

// Validation error codes
export enum ValidationErrorCode {
  REQUIRED_FIELD = 'REQUIRED_FIELD',
  INVALID_PHONE = 'INVALID_PHONE',
  DUPLICATE_CONTACT = 'DUPLICATE_CONTACT',
  INVALID_CATEGORY = 'INVALID_CATEGORY',
  NAME_TOO_LONG = 'NAME_TOO_LONG',
  CATEGORY_EXISTS = 'CATEGORY_EXISTS'
}

/**
 * Enhanced error class with additional context
 */
export class PhonebookError extends Error {
  public readonly type: ErrorType;
  public readonly code: string;
  public readonly userMessage: string;
  public readonly retryable: boolean;
  public readonly suggestions: string[];
  public readonly context?: Record<string, any>;

  constructor(
    type: ErrorType,
    code: string,
    message: string,
    userMessage: string,
    retryable: boolean = false,
    suggestions: string[] = [],
    context?: Record<string, any>
  ) {
    super(message);
    this.name = 'PhonebookError';
    this.type = type;
    this.code = code;
    this.userMessage = userMessage;
    this.retryable = retryable;
    this.suggestions = suggestions;
    this.context = context;
  }
}

/**
 * Parse and categorize errors into user-friendly format
 */
export function parseError(error: any): ErrorDetails {
  // Handle PhonebookError instances
  if (error instanceof PhonebookError) {
    return {
      code: error.code,
      message: error.message,
      userMessage: error.userMessage,
      actionable: error.suggestions.length > 0,
      retryable: error.retryable,
      suggestions: error.suggestions
    };
  }

  // Handle network errors
  if (error.name === 'NetworkError' || error.status) {
    return parseNetworkError(error);
  }

  // Handle file processing errors
  if (error.message?.toLowerCase().includes('file') || 
      error.message?.toLowerCase().includes('parse') ||
      error.message?.toLowerCase().includes('format') ||
      error.message?.toLowerCase().includes('size') ||
      error.message?.toLowerCase().includes('large') ||
      error.message?.toLowerCase().includes('empty') ||
      error.message?.toLowerCase().includes('corrupt')) {
    return parseFileError(error);
  }

  // Handle validation errors
  if (error.message?.toLowerCase().includes('validation') || 
      error.message?.toLowerCase().includes('invalid') ||
      error.message?.toLowerCase().includes('phone') ||
      error.message?.toLowerCase().includes('duplicate') ||
      error.message?.toLowerCase().includes('required') ||
      error.message?.toLowerCase().includes('category')) {
    return parseValidationError(error);
  }

  // Default error handling
  return {
    code: 'UNKNOWN_ERROR',
    message: error.message || 'An unexpected error occurred',
    userMessage: 'Something went wrong. Please try again.',
    actionable: true,
    retryable: true,
    suggestions: ['Try refreshing the page', 'Check your internet connection']
  };
}

/**
 * Parse network-related errors
 */
function parseNetworkError(error: any): ErrorDetails {
  const status = error.status || error.response?.status;
  
  switch (status) {
    case 401:
      return {
        code: NetworkErrorCode.UNAUTHORIZED,
        message: error.message,
        userMessage: 'Your session has expired. Please log in again.',
        actionable: true,
        retryable: false,
        suggestions: ['Log out and log back in', 'Refresh the page']
      };
    
    case 403:
      return {
        code: NetworkErrorCode.FORBIDDEN,
        message: error.message,
        userMessage: 'You don\'t have permission to perform this action.',
        actionable: false,
        retryable: false,
        suggestions: ['Contact your administrator for access']
      };
    
    case 404:
      return {
        code: NetworkErrorCode.NOT_FOUND,
        message: error.message,
        userMessage: 'The requested resource was not found.',
        actionable: true,
        retryable: false,
        suggestions: ['Check if the contact still exists', 'Refresh the page']
      };
    
    case 429:
      return {
        code: NetworkErrorCode.RATE_LIMITED,
        message: error.message,
        userMessage: 'Too many requests. Please wait a moment before trying again.',
        actionable: true,
        retryable: true,
        suggestions: ['Wait a few seconds and try again', 'Reduce the frequency of your actions']
      };
    
    case 500:
    case 502:
    case 503:
    case 504:
      return {
        code: NetworkErrorCode.SERVER_ERROR,
        message: error.message,
        userMessage: 'Server error. Please try again in a moment.',
        actionable: true,
        retryable: true,
        suggestions: ['Try again in a few minutes', 'Check if the service is available']
      };
    
    default:
      if (error.message?.includes('timeout')) {
        return {
          code: NetworkErrorCode.TIMEOUT,
          message: error.message,
          userMessage: 'Request timed out. Please check your connection and try again.',
          actionable: true,
          retryable: true,
          suggestions: ['Check your internet connection', 'Try again with a smaller file']
        };
      }
      
      return {
        code: NetworkErrorCode.CONNECTION_FAILED,
        message: error.message,
        userMessage: 'Connection failed. Please check your internet connection.',
        actionable: true,
        retryable: true,
        suggestions: ['Check your internet connection', 'Try again in a moment']
      };
  }
}

/**
 * Parse file processing errors
 */
function parseFileError(error: any): ErrorDetails {
  const message = error.message?.toLowerCase() || '';
  
  if (message.includes('format') || message.includes('type')) {
    return {
      code: FileErrorCode.INVALID_FORMAT,
      message: error.message,
      userMessage: 'Invalid file format. Please upload an Excel (.xlsx, .xls) or CSV (.csv) file.',
      actionable: true,
      retryable: false,
      suggestions: [
        'Save your file as .xlsx, .xls, or .csv format',
        'Make sure the file is not corrupted'
      ]
    };
  }
  
  if (message.includes('size') || message.includes('large')) {
    return {
      code: FileErrorCode.FILE_TOO_LARGE,
      message: error.message,
      userMessage: 'File is too large. Please upload a file smaller than 10MB.',
      actionable: true,
      retryable: false,
      suggestions: [
        'Reduce the file size by removing unnecessary data',
        'Split large files into smaller chunks'
      ]
    };
  }
  
  if (message.includes('empty')) {
    return {
      code: FileErrorCode.EMPTY_FILE,
      message: error.message,
      userMessage: 'The file appears to be empty or contains no valid data.',
      actionable: true,
      retryable: false,
      suggestions: [
        'Make sure the file contains contact data',
        'Check that the file has proper headers'
      ]
    };
  }
  
  if (message.includes('corrupt') || message.includes('damaged')) {
    return {
      code: FileErrorCode.CORRUPTED_FILE,
      message: error.message,
      userMessage: 'The file appears to be corrupted or damaged.',
      actionable: true,
      retryable: false,
      suggestions: [
        'Try re-exporting the file from the original source',
        'Use a different file format (CSV instead of Excel)'
      ]
    };
  }
  
  return {
    code: FileErrorCode.PARSING_FAILED,
    message: error.message,
    userMessage: 'Failed to process the file. Please check the file format and content.',
    actionable: true,
    retryable: false,
    suggestions: [
      'Make sure the file has proper column headers',
      'Check that contact data is in the expected format',
      'Try saving the file in a different format'
    ]
  };
}

/**
 * Parse validation errors
 */
function parseValidationError(error: any): ErrorDetails {
  const message = error.message?.toLowerCase() || '';
  
  if (message.includes('phone') || message.includes('number')) {
    return {
      code: ValidationErrorCode.INVALID_PHONE,
      message: error.message,
      userMessage: 'Invalid phone number format.',
      actionable: true,
      retryable: false,
      suggestions: [
        'Use international format (+1234567890)',
        'Remove any special characters except + and -',
        'Make sure the number has the correct length'
      ]
    };
  }
  
  if (message.includes('duplicate')) {
    return {
      code: ValidationErrorCode.DUPLICATE_CONTACT,
      message: error.message,
      userMessage: 'A contact with this phone number already exists.',
      actionable: true,
      retryable: false,
      suggestions: [
        'Update the existing contact instead',
        'Use a different phone number',
        'Check if this is the same person'
      ]
    };
  }
  
  if (message.includes('category')) {
    return {
      code: ValidationErrorCode.INVALID_CATEGORY,
      message: error.message,
      userMessage: 'Invalid category selection.',
      actionable: true,
      retryable: false,
      suggestions: [
        'Select a valid category from the list',
        'Create the category first if it doesn\'t exist'
      ]
    };
  }
  
  if (message.includes('required') || message.includes('empty')) {
    return {
      code: ValidationErrorCode.REQUIRED_FIELD,
      message: error.message,
      userMessage: 'Required fields are missing.',
      actionable: true,
      retryable: false,
      suggestions: [
        'Fill in all required fields',
        'Make sure name and phone number are provided'
      ]
    };
  }
  
  return {
    code: 'VALIDATION_ERROR',
    message: error.message,
    userMessage: 'Please check your input and try again.',
    actionable: true,
    retryable: false,
    suggestions: ['Review the form for any errors', 'Make sure all fields are filled correctly']
  };
}

/**
 * Retry mechanism with exponential backoff
 */
export async function withRetry<T>(
  operation: () => Promise<T>,
  config: Partial<RetryConfig> = {}
): Promise<T> {
  const finalConfig = { ...DEFAULT_RETRY_CONFIG, ...config };
  let lastError: any;
  
  for (let attempt = 1; attempt <= finalConfig.maxAttempts; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      
      // Don't retry if error is not retryable
      const errorDetails = parseError(error);
      if (!errorDetails.retryable || attempt === finalConfig.maxAttempts) {
        throw error;
      }
      
      // Calculate delay with exponential backoff
      const delay = Math.min(
        finalConfig.baseDelay * Math.pow(finalConfig.backoffMultiplier, attempt - 1),
        finalConfig.maxDelay
      );
      
      // Add jitter to prevent thundering herd
      const jitteredDelay = delay + Math.random() * 1000;
      
      console.warn(`Operation failed (attempt ${attempt}/${finalConfig.maxAttempts}), retrying in ${Math.round(jitteredDelay)}ms:`, error);
      
      await new Promise(resolve => setTimeout(resolve, jitteredDelay));
    }
  }
  
  throw lastError;
}

/**
 * Create specific error types for common scenarios
 */
export const createFileError = (code: FileErrorCode, details?: string, context?: Record<string, any>) => {
  const errorMap = {
    [FileErrorCode.INVALID_FORMAT]: {
      message: `Invalid file format${details ? `: ${details}` : ''}`,
      userMessage: 'Invalid file format. Please upload an Excel (.xlsx, .xls) or CSV (.csv) file.',
      suggestions: ['Save your file as .xlsx, .xls, or .csv format', 'Make sure the file is not corrupted']
    },
    [FileErrorCode.FILE_TOO_LARGE]: {
      message: `File too large${details ? `: ${details}` : ''}`,
      userMessage: 'File is too large. Please upload a file smaller than 10MB.',
      suggestions: ['Reduce the file size by removing unnecessary data', 'Split large files into smaller chunks']
    },
    [FileErrorCode.CORRUPTED_FILE]: {
      message: `Corrupted file${details ? `: ${details}` : ''}`,
      userMessage: 'The file appears to be corrupted or damaged.',
      suggestions: ['Try re-exporting the file from the original source', 'Use a different file format (CSV instead of Excel)']
    },
    [FileErrorCode.EMPTY_FILE]: {
      message: `Empty file${details ? `: ${details}` : ''}`,
      userMessage: 'The file appears to be empty or contains no valid data.',
      suggestions: ['Make sure the file contains contact data', 'Check that the file has proper headers']
    },
    [FileErrorCode.PARSING_FAILED]: {
      message: `Parsing failed${details ? `: ${details}` : ''}`,
      userMessage: 'Failed to process the file. Please check the file format and content.',
      suggestions: ['Make sure the file has proper column headers', 'Check that contact data is in the expected format']
    },
    [FileErrorCode.UNSUPPORTED_ENCODING]: {
      message: `Unsupported encoding${details ? `: ${details}` : ''}`,
      userMessage: 'The file encoding is not supported. Please save the file with UTF-8 encoding.',
      suggestions: ['Save the file with UTF-8 encoding', 'Try converting the file to CSV format']
    }
  };

  const errorInfo = errorMap[code];
  return new PhonebookError(
    ErrorType.FILE_PROCESSING,
    code,
    errorInfo.message,
    errorInfo.userMessage,
    false,
    errorInfo.suggestions,
    context
  );
};

export const createNetworkError = (code: NetworkErrorCode, details?: string, retryable: boolean = true, context?: Record<string, any>) => {
  const errorMap = {
    [NetworkErrorCode.CONNECTION_FAILED]: {
      message: `Connection failed${details ? `: ${details}` : ''}`,
      userMessage: 'Connection failed. Please check your internet connection.',
      suggestions: ['Check your internet connection', 'Try again in a moment']
    },
    [NetworkErrorCode.TIMEOUT]: {
      message: `Request timeout${details ? `: ${details}` : ''}`,
      userMessage: 'Request timed out. Please check your connection and try again.',
      suggestions: ['Check your internet connection', 'Try again with a smaller file']
    },
    [NetworkErrorCode.SERVER_ERROR]: {
      message: `Server error${details ? `: ${details}` : ''}`,
      userMessage: 'Server error. Please try again in a moment.',
      suggestions: ['Try again in a few minutes', 'Check if the service is available']
    },
    [NetworkErrorCode.UNAUTHORIZED]: {
      message: `Unauthorized${details ? `: ${details}` : ''}`,
      userMessage: 'Your session has expired. Please log in again.',
      suggestions: ['Log out and log back in', 'Refresh the page']
    },
    [NetworkErrorCode.FORBIDDEN]: {
      message: `Forbidden${details ? `: ${details}` : ''}`,
      userMessage: 'You don\'t have permission to perform this action.',
      suggestions: ['Contact your administrator for access']
    },
    [NetworkErrorCode.NOT_FOUND]: {
      message: `Not found${details ? `: ${details}` : ''}`,
      userMessage: 'The requested resource was not found.',
      suggestions: ['Check if the contact still exists', 'Refresh the page']
    },
    [NetworkErrorCode.RATE_LIMITED]: {
      message: `Rate limited${details ? `: ${details}` : ''}`,
      userMessage: 'Too many requests. Please wait a moment before trying again.',
      suggestions: ['Wait a few seconds and try again', 'Reduce the frequency of your actions']
    },
    [NetworkErrorCode.QUOTA_EXCEEDED]: {
      message: `Quota exceeded${details ? `: ${details}` : ''}`,
      userMessage: 'You have exceeded your usage quota. Please try again later.',
      suggestions: ['Wait until your quota resets', 'Contact support to increase your quota']
    }
  };

  const errorInfo = errorMap[code];
  return new PhonebookError(
    ErrorType.NETWORK,
    code,
    errorInfo.message,
    errorInfo.userMessage,
    retryable,
    errorInfo.suggestions,
    context
  );
};

export const createValidationError = (code: ValidationErrorCode, details?: string, context?: Record<string, any>) => {
  const errorMap = {
    [ValidationErrorCode.REQUIRED_FIELD]: {
      message: `Required field missing${details ? `: ${details}` : ''}`,
      userMessage: 'Required fields are missing.',
      suggestions: ['Fill in all required fields', 'Make sure name and phone number are provided']
    },
    [ValidationErrorCode.INVALID_PHONE]: {
      message: `Invalid phone number${details ? `: ${details}` : ''}`,
      userMessage: 'Invalid phone number format.',
      suggestions: ['Use international format (+1234567890)', 'Remove any special characters except + and -']
    },
    [ValidationErrorCode.DUPLICATE_CONTACT]: {
      message: `Duplicate contact${details ? `: ${details}` : ''}`,
      userMessage: 'A contact with this phone number already exists.',
      suggestions: ['Update the existing contact instead', 'Use a different phone number']
    },
    [ValidationErrorCode.INVALID_CATEGORY]: {
      message: `Invalid category${details ? `: ${details}` : ''}`,
      userMessage: 'Invalid category selection.',
      suggestions: ['Select a valid category from the list', 'Create the category first if it doesn\'t exist']
    },
    [ValidationErrorCode.NAME_TOO_LONG]: {
      message: `Name too long${details ? `: ${details}` : ''}`,
      userMessage: 'Contact name is too long.',
      suggestions: ['Use a shorter name (maximum 100 characters)', 'Use abbreviations if necessary']
    },
    [ValidationErrorCode.CATEGORY_EXISTS]: {
      message: `Category already exists${details ? `: ${details}` : ''}`,
      userMessage: 'A category with this name already exists.',
      suggestions: ['Use a different category name', 'Update the existing category instead']
    }
  };

  const errorInfo = errorMap[code];
  return new PhonebookError(
    ErrorType.VALIDATION,
    code,
    errorInfo.message,
    errorInfo.userMessage,
    false,
    errorInfo.suggestions,
    context
  );
};

/**
 * Utility to check if an error is retryable
 */
export function isRetryableError(error: any): boolean {
  const errorDetails = parseError(error);
  return errorDetails.retryable;
}

/**
 * Utility to get user-friendly error message
 */
export function getUserErrorMessage(error: any): string {
  const errorDetails = parseError(error);
  return errorDetails.userMessage;
}

/**
 * Utility to get error suggestions
 */
export function getErrorSuggestions(error: any): string[] {
  const errorDetails = parseError(error);
  return errorDetails.suggestions || [];
}